<template>
    <app-layout :title="$t('Import Tool')">
        <template #header>
            <h2 class="font-semibold text-xl text-gray-800 leading-tight">
                {{ $t('Importer') }}
            </h2>
        </template>


        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 space-y-4 pb-8">
            <div class="overflow-hidden bg-white shadow sm:rounded-lg">
                <div class="px-4 py-5 sm:px-6">
                    <h3 class="text-lg font-medium leading-6 text-gray-900">{{ $t('Import Tool') }}</h3>
                    <p class="mt-1 max-w-2xl text-sm text-gray-500">
                        {{ $t('The lists below show what lies in the import folders right now. Check them before you start an import: files the import cannot place are skipped without a warning.') }}
                    </p>
                </div>
                <div class="border-t border-gray-200 px-4 py-5 sm:px-6">
                    <dl class="text-sm">
                        <dt class="font-bold text-gray-500">{{ $t('Every file name must look like this') }}</dt>
                        <dd class="mt-2">
                            <code class="text-gray-900 break-all">1010_2024_03-Quartalsbericht.pdf</code>
                            <p class="mt-1 text-gray-500">
                                {{ $t('Customer number, year, month, then the document name. A file whose name does not follow this pattern is never imported.') }}
                            </p>
                        </dd>
                    </dl>
                </div>
            </div>

            <AlertBox v-if="!preview.root_exists" type="danger">
                {{ $t('The import folder does not exist on the file server yet.') }}
                <div class="mt-1">
                    <code class="text-xs break-all">{{ preview.root_path }}</code>
                </div>
            </AlertBox>

            <AlertBox v-if="preview.misplaced_root_file_count > 0" type="warning">
                {{ $t('These files lie directly in the import folder instead of in the reports or invoices subfolder, so no import will ever pick them up:') }}
                <ul class="mt-2 space-y-1">
                    <li v-for="file in preview.misplaced_root_files" :key="file">
                        <code class="text-xs break-all">{{ file }}</code>
                    </li>
                </ul>
            </AlertBox>

            <form @submit.prevent="submitForm">
                <div class="overflow-hidden bg-white shadow sm:rounded-lg">
                    <div class="border-gray-200 px-4 py-5 sm:p-0">
                        <dl class="sm:divide-y sm:divide-gray-200">
                            <div class="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-5 sm:px-6">
                                <dt class="text-sm font-bold text-gray-500 flex items-center">
                                    {{ $t('Import type') }}
                                </dt>
                                <dd class="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                                    <Select :show-please-choose="true" :items="selectOptions"
                                            v-model="importForm.type"></Select>
                                    <JetInputError :message="importForm.errors.type" class="mt-2"/>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
                <div class="flex flex-wrap py-4 items-center justify-end gap-3">
                    <p v-if="importForm.type && selectedImportCount === 0"
                       class="mr-auto text-sm font-medium text-danger-700">
                        {{ $t('No file in this folder can be placed, so an import would do nothing.') }}
                    </p>

                    <JetActionMessage :on="importForm.recentlySuccessful" class="mr-3">
                        {{ $t('Imported.') }}
                    </JetActionMessage>

                    <JetButton :class="{ 'opacity-25': importForm.processing || selectedImportCount === 0 }"
                               :disabled="importForm.processing || selectedImportCount === 0">
                        {{ importForm.type
                            ? $t('Import :count files', {count: selectedImportCount})
                            : $t('Start import') }}
                    </JetButton>
                </div>
            </form>

            <div class="flex flex-wrap items-baseline justify-between gap-3 pt-2">
                <h3 class="text-lg font-medium leading-6 text-gray-800">
                    {{ $t('What lies in the folders') }}
                </h3>
                <div class="flex items-center gap-3">
                    <span class="text-sm text-gray-500">{{ $t('Checked at :time', {time: checkedAt}) }}</span>
                    <JetSecondaryButton :disabled="isRechecking" @click="recheck">
                        <svg v-if="isRechecking" class="motion-safe:animate-spin h-4 w-4 mr-2" fill="none"
                             viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                        </svg>
                        {{ $t('Re-check') }}
                    </JetSecondaryButton>
                </div>
            </div>

            <ImportPreviewCard :section="preview.reports" :title="$t('Reports')"/>
            <ImportPreviewCard :section="preview.invoices" :title="$t('Invoices')"/>
        </div>
    </app-layout>
</template>

<script>
import {defineComponent} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import JetButton from '@/Jetstream/Button.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetActionMessage from '@/Jetstream/ActionMessage.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import AlertBox from '@/Components/AlertBox.vue';
import ImportPreviewCard from './Partials/ImportPreviewCard.vue';
import Select from "../../Jetstream/Select";

export default defineComponent({
    components: {
        Select,
        AppLayout,
        JetButton,
        JetSecondaryButton,
        JetActionMessage,
        JetInputError,
        AlertBox,
        ImportPreviewCard,
    },

    watch: {},

    props: {
        preview: Object,
    },

    computed: {
        selectOptions() {
            return [
                {
                    'value': 'report',
                    'label': this.$t('Reports'),
                },
                {
                    'value': 'invoice',
                    'label': this.$t('Invoices'),
                },
            ];
        },

        /**
         * How many files the selected category would actually store. Guards the
         * submit button so an import that can only be a no-op cannot be started.
         */
        selectedImportCount() {
            switch (this.importForm.type) {
                case 'report':
                    return this.preview.reports.would_import_count;
                case 'invoice':
                    return this.preview.invoices.would_import_count;
                default:
                    return 0;
            }
        },

        checkedAt() {
            return new Date(this.preview.generated_at).toLocaleTimeString();
        },
    },

    data() {
        return {
            isRechecking: false,
            importForm: this.$inertia.form({
                type: null,
            }),
        };
    },

    methods: {
        submitForm() {
            this.importForm.put(route('importer.update'), {
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    this.resetForm();
                },
            });
        },
        resetForm() {
            this.importForm.reset();
            this.importForm.clearErrors();
        },
        /**
         * Re-read the folders after fixing file names on the file server, without
         * losing the selected category.
         */
        recheck() {
            this.$inertia.reload({
                only: ['preview'],
                onStart: () => {
                    this.isRechecking = true;
                },
                onFinish: () => {
                    this.isRechecking = false;
                },
            });
        },
    }
})
</script>
